import React, { useMemo, useState } from 'react';
import { Eye } from 'lucide-react';
import DataTable from 'src/shared/components/data-table/DataTable';
import type { DataTableColumns, DataTableState, TableData } from 'src/shared/components/data-table/dataTableTypes';
import { SortingDirection } from 'src/shared/components/data-table/dataTableTypes';
import Show from 'src/shared/components/show/Show';
import DownloadButton from 'src/shared/components/download-button/DownloadButton';
import { LoaderInline } from 'src/shared/components/loader/Loader';
import FileActionsMenu from 'src/shared/components/file-actions-menu/FileActionsMenu';
import { ProjectFile } from 'src/shared/types/graph/project';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import { UploaderType, SiteFileType } from 'src/shared/types';
import { Status } from 'src/shared/types';
import useBoolean from 'src/shared/hooks/useBoolean';
import useAuthService from 'src/shared/hooks/useAuthService';
import usePermissions from 'src/shared/hooks/usePermissions';
import { SecondaryButton } from 'src/shared/components/button/Button';
import { formatUploadDate, dateSortFn, sizeSortFn } from './filesTableHelpers';
import RenameFileModal from './RenameFileModal';
import DeleteFileModal from './DeleteFileModal';
import ViewImageDialog from './ViewImageDialog';

export type FileRow = {
    id: string;
    name: string;
    type: string;
    sizeLabel?: string;
    uploadedAt?: string;
    uploadedBy: UploaderType;
    uploadedByLabel: string;
    awsFileKey?: string;
};

type SnackbarMessage = {
    message: string;
    severity: Status;
};

type FilesTableProps = {
    files: ProjectFile[];
    searchText: string;
    uploadedByFilter: Record<UploaderType, boolean>;
    projectId?: string;
    fileTypeFilter?: Record<SiteFileType, boolean>;
    renameFileMutation: (fileId: string, newName: string) => Promise<any>;
    deleteFileMutation: (fileId: string) => Promise<any>;
    onShowSnackbar: (message: SnackbarMessage) => void;
    downloadMultipleFiles: (fileIds: string[]) => Promise<void>;
    onSingleDownload?: (params: { fileId?: string; fileName?: string }) => void;
    onBulkDownload?: (params: { count: number }) => void;
    onRename?: (params: { oldName?: string; newName: string }) => void;
    onDelete?: (params: { fileId?: string; fileName?: string }) => void;
};

const buttonClassName = 'px-1 py-1 text-sm rounded-sm flex gap-1 h-auto bg-white';
const buttonIconClassName = 'w-[14px] h-[14px]';
const imageButtonIconClassName = 'w-[20px] h-[20px]';

const FilesTable = ({
    files,
    searchText,
    uploadedByFilter,
    projectId,
    fileTypeFilter,
    renameFileMutation,
    deleteFileMutation,
    onShowSnackbar,
    downloadMultipleFiles,
    onSingleDownload,
    onBulkDownload,
    onRename,
    onDelete,
}: FilesTableProps) => {
    const [selectedRowIds, setSelectedRowIds] = useState<Set<string | number>>(new Set());
    const [renameFile, setRenameFile] = useState<FileRow | null>(null);
    const [deleteFile, setDeleteFile] = useState<FileRow | null>(null);
    const [isRenameModalOpen, renameModal] = useBoolean(false);
    const [isDeleteModalOpen, deleteModal] = useBoolean(false);
    const [isDownloading, downloading] = useBoolean(false);
    const [isLightboxOpen, lightboxModal] = useBoolean(false);
    const [lightboxImageData, setLightboxImageData] = useState<FileRow | undefined>(undefined);
    const { currentCustomerName } = useCustomer();
    const { isCurrentUserSuperAdmin } = useAuthService();
    const { isCurrentUserInternal } = usePermissions();

    const getFileTypeCategory = (fileName: string): SiteFileType => {
        const extension = (fileName.split('.').pop() || '').toLowerCase();
        if (['png', 'jpg', 'jpeg'].includes(extension)) {
            return SiteFileType.ImageFiles;
        }
        if (['csv', 'xlsx'].includes(extension)) {
            return SiteFileType.CsvAndxlsxFiles;
        }
        if (extension === 'pdf') {
            return SiteFileType.PdfFiles;
        }
        return SiteFileType.OtherFiles;
    };

    // Transform raw files to rows
    const rows: FileRow[] = useMemo(() => {
        return files.map((f, index) => {
            const ext = (f.name?.split('.').pop() || '').toUpperCase();
            const uploadedAt = f.createdAt;
            const isNatureMetrics = !(f.creatorName && f.creatorName !== UploaderType.NatureMetrics);
            const uploadedBy: UploaderType = isNatureMetrics ? UploaderType.NatureMetrics : UploaderType.Client;
            const uploadedByLabel = isNatureMetrics ? UploaderType.NatureMetrics : `${currentCustomerName} (${f.creatorName})`;
            return {
                id: String(f.id ?? f.awsFileKey ?? index),
                name: f.name,
                type: ext,
                sizeLabel: typeof f.size === 'number' ? `${Math.round(f.size / 1024)} KB` : '',
                uploadedAt,
                uploadedBy,
                uploadedByLabel,
                awsFileKey: f.awsFileKey,
            } as FileRow;
        });
    }, [files]);

    // Filter rows
    const filteredRows = useMemo(() => {
        return rows.filter(row => {
            const isNatureMetrics = row.uploadedBy === UploaderType.NatureMetrics;
            const includeNm = uploadedByFilter[UploaderType.NatureMetrics] && isNatureMetrics;
            const includeClient = uploadedByFilter[UploaderType.Client] && !isNatureMetrics;
            const matchesUploader = includeNm || includeClient;

            if (!fileTypeFilter) {
                return matchesUploader;
            }
            const anyFileTypeSelected = Object.values(fileTypeFilter).some(Boolean);
            const fileTypeCategory = getFileTypeCategory(row.name);
            const matchesFileType = !anyFileTypeSelected || fileTypeFilter[fileTypeCategory];

            const matchesSearch = !searchText || row.name.toLowerCase().includes(searchText.toLowerCase());
            return matchesUploader && matchesFileType && matchesSearch;
        });
    }, [rows, uploadedByFilter, fileTypeFilter, searchText]);

    // Transform to table data
    const tableData: TableData = useMemo(() => {
        return filteredRows.map(row => [
            row.id,
            row.name,
            row.type,
            row.sizeLabel || '',
            formatUploadDate(row.uploadedAt),
            row.uploadedByLabel,
            { data: row },
        ]);
    }, [filteredRows]);

    const handleTableStateChange = (newState: DataTableState) => {
        setSelectedRowIds(newState.selectedRowIds);
    };

    const handleDownloadRows = async (rowsToDownload: FileRow[]) => {
        if (rowsToDownload.length === 0) {
            return;
        }
        try {
            downloading.on();
            const ids = rowsToDownload.map(r => String(r.id));
            await downloadMultipleFiles(ids);
            if (rowsToDownload.length === 1) {
                const r = rowsToDownload[0];
                if (onSingleDownload) {
                    onSingleDownload({ fileId: r.id as string, fileName: r.name });
                }
            } else if (onBulkDownload) {
                onBulkDownload({ count: rowsToDownload.length });
            }
        } catch (e) {
            console.error(e);
        } finally {
            downloading.off();
        }
    };

    const handleBulkDownload = async () => {
        const selectedFiles = filteredRows.filter(row => selectedRowIds.has(row.id));
        await handleDownloadRows(selectedFiles);
    };

    const handleRename = (row: FileRow) => {
        setRenameFile(row);
        renameModal.on();
    };

    const handleRenameConfirm = async (newName: string) => {
        if (!renameFile) {
            return;
        }

        try {
            await renameFileMutation(renameFile.id, newName);

            onShowSnackbar({
                message: 'File renamed successfully',
                severity: Status.SUCCESS,
            });
            if (onRename) {
                onRename({ oldName: renameFile.name, newName });
            }
            renameModal.off();
            setRenameFile(null);
        } catch (_error) {
            onShowSnackbar({
                message: 'Failed to rename file. Please try again.',
                severity: Status.ERROR,
            });
        }
    };

    const handleDelete = (row: FileRow) => {
        setDeleteFile(row);
        deleteModal.on();
    };

    const handleDeleteConfirm = async () => {
        if (!deleteFile) {
            return;
        }

        try {
            await deleteFileMutation(deleteFile.id);

            onShowSnackbar({
                message: 'File deleted successfully',
                severity: Status.SUCCESS,
            });
            if (onDelete) {
                onDelete({ fileId: deleteFile.id as string, fileName: deleteFile.name });
            }
            deleteModal.off();
            setDeleteFile(null);
        } catch (_error) {
            onShowSnackbar({
                message: 'Failed to delete file. Please try again.',
                severity: Status.ERROR,
            });
        }
    };

    const isDeleteDisabled = (row: FileRow) => {
        const isSuperAdminInternal = isCurrentUserSuperAdmin && isCurrentUserInternal;
        return row.uploadedBy === UploaderType.NatureMetrics && !isSuperAdminInternal;
    };

    const hasMultipleSelections = selectedRowIds.size >= 2;

    // Create table state
    const tableState: Partial<DataTableState> = useMemo(
        () => ({
            data: tableData,
            rowsPerPage: Infinity,
            searchText,
            sortedColumn: { columnId: 'uploadedAt', sortedDirection: SortingDirection.DESC },
            hiddenColumnIds: new Set<string | number>(['id']),
        }),
        [tableData, searchText]
    );

    const columns: DataTableColumns = [
        {
            columnId: 'id',
            title: 'ID',
            isSearchable: false,
            isUnique: true,
            isHideable: false,
        },
        {
            columnId: 'name',
            title: 'File name',
            isSortable: true,
            isSearchable: true,
        },
        { columnId: 'type', title: 'Type', width: '120px', isSortable: true },
        { columnId: 'size', title: 'Size', width: '120px', isSortable: true, sortFn: sizeSortFn },
        {
            columnId: 'uploadedAt',
            title: 'Upload date',
            width: '160px',
            isSortable: true,
            sortFn: dateSortFn,
        },
        { columnId: 'uploadedBy', title: 'Uploaded by', width: '160px', isSortable: true },
        {
            columnId: 'actions',
            title: 'Actions',
            width: '200px',
            isSearchable: false,
            renderer: ({ cellData }) => {
                const row = (cellData as any)?.data as FileRow;
                const disabled = isDeleteDisabled(row);
                const fileExtension = (row.name.split('.').pop() || '').toLowerCase();
                const isImage = ['png', 'jpg', 'jpeg'].includes(fileExtension);

                return (
                    <div className='flex items-center gap-2'>
                        <DownloadButton
                            buttonClassName={buttonClassName}
                            iconClassName={isImage ? imageButtonIconClassName : buttonIconClassName}
                            displayLabel={!isImage}
                            onClick={() => handleDownloadRows([row])}
                        />
                        {isImage && (
                            <SecondaryButton
                                className={buttonClassName}
                                onClick={() => {
                                    setLightboxImageData(row);
                                    lightboxModal.on();
                                }}
                            >
                                view <Eye className={buttonIconClassName} />
                            </SecondaryButton>
                        )}
                        <FileActionsMenu
                            onRename={() => handleRename(row)}
                            onDelete={() => handleDelete(row)}
                            isDeleteDisabled={disabled}
                        />
                    </div>
                );
            },
        },
    ];

    return (
        <>
            <div className='mt-2'>
                <div className='flex items-center gap-4 mb-2 h-12'>
                    <div className='text-sm'>{`Displaying ${filteredRows.length} of ${rows.length} files`}</div>
                    <Show when={hasMultipleSelections}>
                        <div className='flex items-center gap-4 bg-grey-20 px-4 py-1 rounded-lg'>
                            <div className='text-sm font-medium'>{`${selectedRowIds.size} files selected`}</div>
                            <DownloadButton
                                buttonClassName={buttonClassName}
                                iconClassName={buttonIconClassName}
                                onClick={handleBulkDownload}
                                className='bg-white'
                            >
                                {isDownloading && (
                                    <div className='flex items-center gap-2'>
                                        <span className='ml-[6px]'>Preparing</span>
                                        <LoaderInline size={12} />
                                    </div>
                                )}
                            </DownloadButton>
                        </div>
                    </Show>
                </div>
                <div className='[&_tbody_tr:not([class*="rowSelected"]):hover]:bg-white [&_tbody_tr:not([class*="rowSelected"]):active]:bg-white'>
                    <DataTable
                        state={tableState}
                        columns={columns}
                        allowSortClear={false}
                        isSelectable={true}
                        selectableColumnIndex={0}
                        onStateChange={handleTableStateChange}
                        emptyStateMessage='No files found'
                    />
                </div>
            </div>
            {isRenameModalOpen && renameFile && (
                <RenameFileModal
                    file={renameFile}
                    onClose={() => {
                        renameModal.off();
                        setRenameFile(null);
                    }}
                    onSave={handleRenameConfirm}
                />
            )}
            {isDeleteModalOpen && deleteFile && (
                <DeleteFileModal
                    file={deleteFile}
                    onClose={() => {
                        deleteModal.off();
                        setDeleteFile(null);
                    }}
                    onDelete={handleDeleteConfirm}
                />
            )}
            <ViewImageDialog
                isOpen={isLightboxOpen}
                onClose={() => {
                    lightboxModal.off();
                    setLightboxImageData(undefined);
                }}
                imageData={lightboxImageData!}
                projectId={projectId!}
            />
        </>
    );
};

export default FilesTable;
